import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import OrganizerForm from '../Organizers/OrganizerForm';
import { pushModalToStack, removeModalFromStack, isTopModal } from './modalStack';
import UnsavedChangesPopover from './UnsavedChangesPopover';

const OrganizerFormModal = ({ organizer, onSave, onCancel, fixedType = 'specialized_org' }) => {
  const inferEntityType = (value) => {
    if (!value) return 'legal';
    if (value.type === 'arbitrage_manager') return 'individual';
    const explicit = String(value.entity_type || '').trim();
    if (explicit) return explicit;
    const legalForm = String(value.legal_form || '').trim().toUpperCase();
    if (legalForm === 'ИП') return 'entrepreneur';
    const ogrnDigits = String(value.ogrn || '').replace(/\D/g, '');
    if (ogrnDigits.length === 15) return 'entrepreneur';
    return 'legal';
  };

  const derivedEntityType = organizer
    ? inferEntityType(organizer)
    : 'legal';
  const initialData = {
    name: organizer?.name || '',
    type: fixedType || organizer?.type || 'specialized_org',
    entity_type: fixedType === 'arbitrage_manager' ? 'individual' : derivedEntityType,
    legal_form: derivedEntityType === 'entrepreneur' ? 'ИП' : (organizer?.legal_form || ''),
    inn: organizer?.inn || '',
    kpp: organizer?.kpp || '',
    ogrn: organizer?.ogrn || '',
    contacts: organizer?.contacts || '',
    sro_info: organizer?.sro_info || '',
    address: organizer?.address || '',
    phone: organizer?.phone || '',
    email: organizer?.email || '',
    mailing_address: organizer?.mailing_address || '',
    executive_role: organizer?.executive_role || (derivedEntityType === 'legal' ? 'Генеральный директор' : ''),
    executive_name: organizer?.executive_name || '',
    snils: organizer?.snils || '',
    bank_details: organizer?.bank_details || '',
    details: organizer?.details || '',
    sro_name: organizer?.sro_name || '',
    sro_inn: organizer?.sro_inn || '',
    sro_ogrn: organizer?.sro_ogrn || '',
    sro_address: organizer?.sro_address || '',
  };

  const [formData, setFormData] = useState(initialData);
  const [initialFormData, setInitialFormData] = useState(initialData);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const modalIdRef = useRef(`organizer_modal_${Date.now()}_${Math.random().toString(16).slice(2)}`);

  // Обновление формы при изменении organizer prop
  useEffect(() => {
    const nextDerivedEntityType = organizer
      ? inferEntityType(organizer)
      : 'legal';
    const newData = {
      name: organizer?.name || '',
      type: fixedType || organizer?.type || 'specialized_org',
      entity_type: fixedType === 'arbitrage_manager' ? 'individual' : nextDerivedEntityType,
      legal_form: nextDerivedEntityType === 'entrepreneur' ? 'ИП' : (organizer?.legal_form || ''),
      inn: organizer?.inn || '',
      kpp: organizer?.kpp || '',
      ogrn: organizer?.ogrn || '',
      contacts: organizer?.contacts || '',
      sro_info: organizer?.sro_info || '',
      address: organizer?.address || '',
      phone: organizer?.phone || '',
      email: organizer?.email || '',
      mailing_address: organizer?.mailing_address || '',
      executive_role: organizer?.executive_role || (nextDerivedEntityType === 'legal' ? 'Генеральный директор' : ''),
      executive_name: organizer?.executive_name || '',
      snils: organizer?.snils || '',
      bank_details: organizer?.bank_details || '',
      details: organizer?.details || '',
      sro_name: organizer?.sro_name || '',
      sro_inn: organizer?.sro_inn || '',
      sro_ogrn: organizer?.sro_ogrn || '',
      sro_address: organizer?.sro_address || '',
    };
    setFormData(newData);
    setInitialFormData(JSON.parse(JSON.stringify(newData)));
  }, [organizer, fixedType]);

  // Проверка наличия изменений
  const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);

  useEffect(() => {
    const id = modalIdRef.current;
    pushModalToStack(id);
    return () => removeModalFromStack(id);
  }, []);

  // Обработка Escape с приоритетом
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (!isTopModal(modalIdRef.current)) return;
        if (showConfirmDialog) {
          return;
        }
        try {
          if (document.body.classList.contains('aot-searchable-select-open')) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            document.dispatchEvent(new Event('aot-close-searchable-select'));
            return false;
          }
        } catch (_) {}
        
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleClose();
        return false;
      }
    };

    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [hasChanges, showConfirmDialog]);

  // Обработка Escape для диалога подтверждения
  useEffect(() => {
    if (showConfirmDialog) {
      const handleConfirmEscape = (e) => {
        if (e.key === 'Escape') {
          if (!isTopModal(modalIdRef.current)) return;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setShowConfirmDialog(false);
          return false;
        }
      };

      document.addEventListener('keydown', handleConfirmEscape, { capture: true });
      return () => {
        document.removeEventListener('keydown', handleConfirmEscape, { capture: true });
      };
    }
  }, [showConfirmDialog]);

  const handleClose = () => {
    if (hasChanges) {
      setShowConfirmDialog(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onCancel();
  };

  const handleSave = async (data, { exit } = {}) => {
    try {
      const result = await onSave(data, { exit });

      if (result === false) {
        return false;
      }

      const payload = typeof result === 'object' && result !== null ? result : data;
      const snapshot = JSON.parse(JSON.stringify(payload));
      setFormData(snapshot);
      setInitialFormData(snapshot);

      if (exit) {
        setShowConfirmDialog(false);
        onCancel();
      }

      return result;
    } catch (error) {
      console.error('Error saving organizer:', error);
      return false;
    }
  };

  const modalContent = (
    <>
      <div className="modal-overlay entity-form-modal-overlay" style={{ zIndex: 3200 }}>
        <div className="modal modal-lg">
          <div className="modal-header">
            <h3>{organizer ? 'Редактирование организатора торгов' : 'Создание организатора торгов'}</h3>
            <button className="close-btn" onClick={handleClose}>&times;</button>
          </div>
          <div className="modal-body">
            <OrganizerForm
              organizer={organizer}
              formData={formData}
              setFormData={setFormData}
              onSave={handleSave}
              onCancel={handleClose}
              isViewMode={false}
              fixedType={fixedType}
            />
          </div>
        </div>
      </div>

      <UnsavedChangesPopover
        isOpen={showConfirmDialog}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmClose}
        zIndex={13300}
      />
    </>
  );

  return createPortal(modalContent, document.body);
};

export default OrganizerFormModal;
